import Api from '../api';
import {
  GET_MY_WALLETS, GET_MY_WALLETS_URL,
  GET_MY_TRANSACTIONS, GET_MY_TRANSACTIONS_URL,
  TOPUP_MY_WALLET, TOPUP_MY_WALLET_URL,
  ADMIN_GET_CUSTOMER_WALLETS, ADMIN_GET_CUSTOMER_WALLETS_URL,
  ADMIN_GET_CUSTOMER_TRANSACTIONS, ADMIN_GET_CUSTOMER_TRANSACTIONS_URL,
  ADMIN_CREATE_WALLETS, ADMIN_CREATE_WALLETS_URL,
  ADMIN_CREATE_CUSTOM_WALLET, ADMIN_CREATE_CUSTOM_WALLET_URL,
  ADMIN_TOPUP_WALLET, ADMIN_TOPUP_WALLET_URL,
  ADMIN_TERMINATE_WALLET, ADMIN_TERMINATE_WALLET_URL,
  ADMIN_LIST_PLANS, ADMIN_LIST_PLANS_URL,
  ADMIN_CREATE_PLAN, ADMIN_CREATE_PLAN_URL,
  ADMIN_UPDATE_PLAN, ADMIN_UPDATE_PLAN_URL,
  ADMIN_DELETE_PLAN, ADMIN_DELETE_PLAN_URL,
  ADMIN_CHANGE_SUBSCRIPTION, ADMIN_CHANGE_SUBSCRIPTION_URL,
  ADMIN_GET_SUBSCRIPTION, ADMIN_GET_SUBSCRIPTION_URL,
  ADMIN_GET_BILLING_SETUP, ADMIN_GET_BILLING_SETUP_URL,
  ADMIN_RUN_BILLING_SETUP, ADMIN_RUN_BILLING_SETUP_URL,
  ADMIN_RUN_SMS_CONFIG_SETUP, ADMIN_RUN_SMS_CONFIG_SETUP_URL,
  ADMIN_RUN_LIFECYCLE_SETUP, ADMIN_RUN_LIFECYCLE_SETUP_URL,
  ADMIN_EDIT_WALLET, ADMIN_EDIT_WALLET_URL,
  ADMIN_VOID_WALLET_CREDITS, ADMIN_VOID_WALLET_CREDITS_URL,
} from '../lib/constant';
import {handleAPIDispatch} from '../lib/utils';

// ── Self-service (customer) ──────────────────────────

// Get all wallets for the current channel (from auth token)
export function getMyWallets() {
  const request = Api.get(GET_MY_WALLETS_URL);
  return handleAPIDispatch(request, GET_MY_WALLETS);
}

// Get transactions (credit history), optionally filtered by wallet_name and transaction_type
// Every billing EVENT — top-ups, grants, refunds, voids, migrated and legacy rows —
// and none of the per-window SMS-usage debits (transaction_status 0).
export const BILLING_EVENT_STATUSES = ['purchased', 'granted', 'voided'];

// transactionStatuses: array of purchased|granted|voided|invoiced; [] = no filter
export function getMyTransactions(walletName, transactionType, page = 1, perPage = 100, transactionStatuses = []) {
  let url = GET_MY_TRANSACTIONS_URL + `?pagination.page=${page}&pagination.per_page=${perPage}`;
  if (walletName) {
    url += `&wallet_name=${walletName}`;
  }
  if (transactionType) {
    url += `&transaction_type=${transactionType}`;
  }
  (transactionStatuses || []).forEach(s => { url += `&transaction_statuses=${s}`; });
  const request = Api.get(url);
  return handleAPIDispatch(request, GET_MY_TRANSACTIONS);
}

// Top up a specific wallet
export function topUpMyWallet(lagoId, paidCredits, name) {
  const url = TOPUP_MY_WALLET_URL.replace('{lagoId}', lagoId);
  const body = { paid_credits: String(paidCredits) };
  if (name) body.name = name;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, TOPUP_MY_WALLET);
}

// ── Admin / Reseller ─────────────────────────────────

// Get all wallets for a customer (admin view)
export function adminGetCustomerWallets(orgId, channel) {
  let url = ADMIN_GET_CUSTOMER_WALLETS_URL.replace('{orgId}', orgId);
  if (channel) {
    url += `?channel=${channel}`;
  }
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_GET_CUSTOMER_WALLETS);
}

// Get transactions for a customer (admin view)
export function adminGetCustomerTransactions(orgId, walletName, page = 1, perPage = 100, transactionStatuses = BILLING_EVENT_STATUSES) {
  let url = ADMIN_GET_CUSTOMER_TRANSACTIONS_URL.replace('{orgId}', orgId);
  url += `?pagination.page=${page}&pagination.per_page=${perPage}`;
  if (walletName) {
    url += `&wallet_name=${walletName}`;
  }
  (transactionStatuses || []).forEach(s => { url += `&transaction_statuses=${s}`; });
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_GET_CUSTOMER_TRANSACTIONS);
}

// Create wallets for a customer (admin)
export function adminCreateWallets(orgId, channel, messageTypes, initialCredits = 0, rateAmount, remarks) {
  const url = ADMIN_CREATE_WALLETS_URL.replace('{orgId}', orgId);
  const body = {
    channel,
    message_types: messageTypes,
    initial_credits: initialCredits,
  };
  if (rateAmount) body.rate_amount = rateAmount;
  if (remarks) body.transaction_name = remarks;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_CREATE_WALLETS);
}

// Create a single custom-rate wallet (admin — volume deal / negotiated rate)
export function adminCreateCustomWallet(orgId, name, rateAmount, paidCredits, currency, priority, channel, messageType, remarks) {
  const url = ADMIN_CREATE_CUSTOM_WALLET_URL.replace('{orgId}', orgId);
  const body = {
    name,
    rate_amount: rateAmount,
    paid_credits: String(paidCredits),
    priority,
    channel,
    message_type: messageType,
  };
  if (currency) body.currency = currency;
  if (remarks) body.transaction_name = remarks;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_CREATE_CUSTOM_WALLET);
}

// Top up a customer's wallet (admin)
export function adminTopUpWallet(orgId, lagoId, paidCredits, name) {
  const url = ADMIN_TOPUP_WALLET_URL
    .replace('{orgId}', orgId)
    .replace('{lagoId}', lagoId);
  const body = { paid_credits: String(paidCredits) };
  if (name) body.name = name;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_TOPUP_WALLET);
}

// Terminate a wallet (admin)
export function adminTerminateWallet(orgId, lagoId) {
  const url = ADMIN_TERMINATE_WALLET_URL
    .replace('{orgId}', orgId)
    .replace('{lagoId}', lagoId);
  const request = Api.post(url, {});
  return handleAPIDispatch(request, ADMIN_TERMINATE_WALLET);
}

// List available billing plans (admin)
export function adminListPlans() {
  const request = Api.get(ADMIN_LIST_PLANS_URL);
  return handleAPIDispatch(request, ADMIN_LIST_PLANS);
}

// Create a new billing plan (admin)
export function adminCreatePlan(body) {
  const request = Api.post(ADMIN_CREATE_PLAN_URL, body);
  return handleAPIDispatch(request, ADMIN_CREATE_PLAN);
}

// Update an existing billing plan (admin)
export function adminUpdatePlan(planCode, body) {
  const url = ADMIN_UPDATE_PLAN_URL.replace('{planCode}', planCode);
  const request = Api.put(url, body);
  return handleAPIDispatch(request, ADMIN_UPDATE_PLAN);
}

// Delete a billing plan (admin)
export function adminDeletePlan(planCode) {
  const url = ADMIN_DELETE_PLAN_URL.replace('{planCode}', planCode);
  const request = Api.delete(url);
  return handleAPIDispatch(request, ADMIN_DELETE_PLAN);
}

// Edit a wallet (admin) — update name, priority, etc.
export function adminEditWallet(orgId, lagoId, body) {
  const url = ADMIN_EDIT_WALLET_URL
    .replace('{orgId}', orgId)
    .replace('{lagoId}', lagoId);
  const request = Api.put(url, body);
  return handleAPIDispatch(request, ADMIN_EDIT_WALLET);
}

// Void credits from a wallet (admin)
export function adminVoidWalletCredits(orgId, lagoId, voidedCredits, remarks) {
  const url = ADMIN_VOID_WALLET_CREDITS_URL
    .replace('{orgId}', orgId)
    .replace('{lagoId}', lagoId);
  const body = { voided_credits: String(voidedCredits) };
  if (remarks) body.name = remarks;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_VOID_WALLET_CREDITS);
}

// Get subscription for a customer (admin)
export function adminGetSubscription(orgId) {
  const url = ADMIN_GET_SUBSCRIPTION_URL.replace('{orgId}', orgId);
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_GET_SUBSCRIPTION);
}

// Create or change subscription plan for a customer (admin)
export function adminChangeSubscription(orgId, planCode, customSmsRate) {
  const url = ADMIN_CHANGE_SUBSCRIPTION_URL.replace('{orgId}', orgId);
  const body = { plan_code: planCode };
  if (customSmsRate) body.custom_sms_rate = customSmsRate;
  const request = Api.post(url, body);
  return handleAPIDispatch(request, ADMIN_CHANGE_SUBSCRIPTION);
}

// ── Admin Billing Setup (Onboarding) ────────────────

// Get billing setup status for a customer
export function adminGetBillingSetup(orgId) {
  const url = ADMIN_GET_BILLING_SETUP_URL.replace('{orgId}', orgId);
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_GET_BILLING_SETUP);
}

// Run billing setup for a customer
export function adminRunBillingSetup(orgId) {
  const url = ADMIN_RUN_BILLING_SETUP_URL.replace('{orgId}', orgId);
  const request = Api.post(url, {});
  return handleAPIDispatch(request, ADMIN_RUN_BILLING_SETUP);
}

// Run SMS config setup for a customer (seeds sender ids / message types /
// templates from the trial seed — the SMS analog of adminRunBillingSetup)
export function adminRunSmsConfigSetup(orgId) {
  const url = ADMIN_RUN_SMS_CONFIG_SETUP_URL.replace('{orgId}', orgId);
  const request = Api.post(url, {});
  return handleAPIDispatch(request, ADMIN_RUN_SMS_CONFIG_SETUP);
}

// Run lifecycle setup for a customer: create the billing-layer lifecycle
// account in TRIAL for a tenant that bypassed onboarding's stepLifecycle.
// Billing layer only — does not touch IAM state or rate-limit tier.
export function adminRunLifecycleSetup(orgId) {
  const url = ADMIN_RUN_LIFECYCLE_SETUP_URL.replace('{orgId}', orgId);
  const request = Api.post(url, {});
  return handleAPIDispatch(request, ADMIN_RUN_LIFECYCLE_SETUP);
}
